import {
  JOURNEY_ALREADY_RUNNING_CODE,
  type JourneyCreateRequest,
  type JourneyPlanningRecord,
  type JourneyPlanningRecordsResponse,
  type JourneyTypeRecord,
  type JourneyTypeRecordsResponse,
  type JourneyTypeRequest,
} from '@wundo/shared/journey.ts';
import { ApiError, apiFetch, apiFetchJson } from './api.ts';

/*
 * One organisation's journey types and journeys, addressed by organisation:
 * Wundo staff are not a member of the organisation they set these up for.
 * Planning only — never what was said in a journey's meetings.
 */

export async function listJourneyTypes(
  organisationId: string,
): Promise<JourneyTypeRecord[]> {
  const { data } = await apiFetchJson<JourneyTypeRecordsResponse>(
    `/organisations/${organisationId}/journey-types`,
  );
  return data;
}

export function readJourneyType(
  organisationId: string,
  typeId: string,
): Promise<JourneyTypeRecord> {
  return apiFetchJson<JourneyTypeRecord>(
    `/organisations/${organisationId}/journey-types/${typeId}`,
  );
}

export function createJourneyType(
  organisationId: string,
  body: JourneyTypeRequest,
): Promise<JourneyTypeRecord> {
  return apiFetchJson<JourneyTypeRecord>(
    `/organisations/${organisationId}/journey-types`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

/** The whole type, steps included: it is saved as one record. */
export function updateJourneyType(
  organisationId: string,
  typeId: string,
  body: JourneyTypeRequest,
): Promise<JourneyTypeRecord> {
  return apiFetchJson<JourneyTypeRecord>(
    `/organisations/${organisationId}/journey-types/${typeId}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
  );
}

export async function deleteJourneyType(
  organisationId: string,
  typeId: string,
): Promise<void> {
  await apiFetch(`/organisations/${organisationId}/journey-types/${typeId}`, {
    method: 'DELETE',
  });
}

export async function listJourneys(
  organisationId: string,
): Promise<JourneyPlanningRecord[]> {
  const { data } = await apiFetchJson<JourneyPlanningRecordsResponse>(
    `/organisations/${organisationId}/journeys`,
  );
  return data;
}

export function readJourney(
  organisationId: string,
  journeyId: string,
): Promise<JourneyPlanningRecord> {
  return apiFetchJson<JourneyPlanningRecord>(
    `/organisations/${organisationId}/journeys/${journeyId}`,
  );
}

/** Refused when the pair already runs one: `runningJourneyIdOf` names that journey. */
export function createJourney(
  organisationId: string,
  body: JourneyCreateRequest,
): Promise<JourneyPlanningRecord> {
  return apiFetchJson<JourneyPlanningRecord>(
    `/organisations/${organisationId}/journeys`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

/** The journey stays, marked stopped; nothing about it is followed up any more. */
export async function stopJourney(
  organisationId: string,
  journeyId: string,
): Promise<void> {
  await apiFetch(`/organisations/${organisationId}/journeys/${journeyId}/actions/stop`, {
    method: 'POST',
  });
}

export async function deleteJourney(
  organisationId: string,
  journeyId: string,
): Promise<void> {
  await apiFetch(`/organisations/${organisationId}/journeys/${journeyId}`, {
    method: 'DELETE',
  });
}

/**
 * The running journey a create collided with, when that is why it was
 * refused: the one to stop first. Null for every other failure.
 */
export function runningJourneyIdOf(failure: unknown): string | null {
  if (!(failure instanceof ApiError) || failure.code !== JOURNEY_ALREADY_RUNNING_CODE) {
    return null;
  }
  if (typeof failure.body !== 'object' || failure.body === null) return null;

  const { existing_journey_id: journeyId } = failure.body as Record<string, unknown>;
  return typeof journeyId === 'string' ? journeyId : null;
}
